import { Component, signal, computed, effect, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MapPickerButtonComponent } from '../../shared/components/map-picker-dialog-component/mapPickerButtonComponent';
import { CheckoutFormState } from '../../core/models/interfaces-general';
import { MapPickerResult } from '../../services/mapPickerService-service';
import { CartService } from '../../services/cart.service';
import { PedidoService } from '../../core/services/pedido.service';
import { PagoService } from '../../core/services/pago.service';
import { EmpresaService } from '../../core/services/empresa.service';
import { MetodoPagoApi } from '../../core/models/api/empresa-api';
import { ConfirmarPedidoPayload } from '../../core/models/api/pedido-api';

@Component({
    selector: 'app-checkout-stepper',
    standalone: true,
    imports: [CommonModule, FormsModule, MapPickerButtonComponent],
    templateUrl: './checkout-stepper.component.html'
})
export class CheckoutStepperComponent implements OnInit {
    cartService = inject(CartService);
    private router = inject(Router);
    private pedidos = inject(PedidoService);
    private pagos = inject(PagoService);
    private empresa = inject(EmpresaService);

    // 1. SIGNAL PRINCIPAL DE ESTADO
    currentStep = signal<number>(1);

    form = signal<CheckoutFormState>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        voucherType: 'boleta',
        docNumber: '',
        deliveryType: 'delivery',
        recipientType: 'other',
        recipientName: '',
        recipientLastName: '',
        recipientPhone: '',
        cardMessage: '',
        address: '',
        reference: '',
        location: undefined,
        paymentMethod: null,
        voucherFile: null,
        voucherFileName: null,
        termsAccepted: false
    });

    costoEnvio = signal<number>(0);
    metodosPago = signal<MetodoPagoApi[]>([]);
    submitting = signal<boolean>(false);
    submitError = signal<string | null>(null);
    pedidoUuid = signal<string | null>(null);
    pedidoNumero = signal('');
    pedidoEmail = signal('');
    pedidoTotal = signal(0);
    copiado = signal(false);

    // 2. COMPUTEDS PARA VALIDACIÓN PASO A PASO
    step1Valid = computed(() => {
        const f = this.form();
        const docLen = f.voucherType === 'boleta' ? 8 : 11;
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
        const isPhoneValid = /^[0-9]{9}$/.test(f.phone);

        return (
            f.firstName.trim().length > 1 &&
            f.lastName.trim().length > 1 &&
            isPhoneValid &&
            isEmailValid &&
            f.docNumber.trim().length === docLen
        );
    });

    step2Valid = computed(() => {
        const f = this.form();
        if (f.deliveryType === 'pickup') return true;

        const recipientValid = f.recipientType === 'self' || (
            f.recipientName.trim().length > 1 &&
            f.recipientLastName.trim().length > 1 &&
            /^[0-9]{9}$/.test(f.recipientPhone)
        );

        return recipientValid && f.address.trim().length > 3;
    });

    step3Valid = computed(() => {
        const f = this.form();
        return (
            f.paymentMethod !== null &&
            f.voucherFile !== null &&
            f.termsAccepted === true
        );
    });

    cartSubtotal = computed(() => this.cartService.subtotal());

    metodoSeleccionado = computed(() => {
        const want = (this.form().paymentMethod ?? '').toLowerCase();
        const list = this.metodosPago();
        return list.find((m) => (m.tipoMetodoPago ?? '').toLowerCase().includes(want)) ?? list[0] ?? null;
    });

    orderTotal = computed(() => {
        const shipping = this.form().deliveryType === 'delivery' ? this.costoEnvio() : 0;
        return this.cartSubtotal() + shipping;
    });

    // 3. EFFECT PARA AUDITORÍA / SINCRONIZACIÓN DE MAPA Y DIRECCIÓN
    constructor() {
        effect(() => {
            const loc = this.form().location;
            if (loc?.address && !this.form().address) {
                this.updateField('address', loc.address);
            }
        });
    }

    ngOnInit(): void {
        this.empresa.getEmpresa().subscribe({
            next: (e) => {
                this.costoEnvio.set(e?.costoEnvioDefault ?? 0);
            },
            error: () => this.costoEnvio.set(0)
        });
        this.empresa.listMetodosPago().subscribe({
            next: (m) => this.metodosPago.set(m),
            error: () => this.metodosPago.set([])
        });
    }

    // MÉTODOS DE MANEJO
    updateField<K extends keyof CheckoutFormState>(key: K, value: CheckoutFormState[K]) {
        this.form.update(prev => ({ ...prev, [key]: value }));
    }

    onLocationSelected(result: MapPickerResult) {
        this.form.update(prev => ({
            ...prev,
            location: result,
            address: result.address || prev.address
        }));
    }

    onFileSelected(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            const file = target.files[0];
            this.form.update(prev => ({
                ...prev,
                voucherFile: file,
                voucherFileName: file.name
            }));
        }
    }

    nextStep() {
        if (this.currentStep() === 1 && this.step1Valid()) {
            this.currentStep.set(2);
        } else if (this.currentStep() === 2 && this.step2Valid()) {
            this.currentStep.set(3);
        }
    }

    prevStep() {
        if (this.currentStep() > 1) {
            this.currentStep.update(s => s - 1);
        }
    }

    goToStep(step: number) {
        if (step === 1) this.currentStep.set(1);
        if (step === 2 && this.step1Valid()) this.currentStep.set(2);
        if (step === 3 && this.step1Valid() && this.step2Valid()) this.currentStep.set(3);
    }

    submitFinalOrder() {
        if (!this.step1Valid() || !this.step2Valid() || !this.step3Valid() || this.submitting()) {
            return;
        }
        const cartUuid = this.cartService.cartUuid();
        const voucher = this.form().voucherFile;
        if (!cartUuid || !voucher) {
            this.submitError.set('Tu carrito expiró. Vuelve a agregar tus productos.');
            return;
        }
        const metodoUuid = this.resolveMetodoPagoUuid();
        if (!metodoUuid) {
            this.submitError.set('No hay un medio de pago disponible para tu elección.');
            return;
        }

        this.submitError.set(null);
        this.submitting.set(true);

        const email = this.form().email.trim();
        const total = this.orderTotal();
        this.pedidos.confirmarPedido(this.buildPayload(cartUuid, metodoUuid)).subscribe({
            next: (tx) => {
                this.pagos.pagarConVoucher(tx.pedidoUuid, voucher).subscribe({
                    next: () => {
                        this.submitting.set(false);
                        this.mostrarExito(tx.pedidoUuid, tx.numeroPedido, email, total);
                        this.cartService.clearCart();
                    },
                    error: (err) => {
                        this.submitting.set(false);
                        this.mostrarExito(tx.pedidoUuid, tx.numeroPedido, email, total);
                        this.submitError.set(
                            'Tu pedido se registró, pero no pudimos subir el comprobante: ' +
                            (err?.message || 'inténtalo desde Mis pedidos.')
                        );
                        this.cartService.clearCart();
                    }
                });
            },
            error: (err) => {
                this.submitting.set(false);
                this.submitError.set(err?.message || 'No se pudo confirmar tu pedido.');
            }
        });
    }

    mostrarExito(pedidoUuid: string, numero: string, email: string, total: number) {
        this.pedidoUuid.set(pedidoUuid);
        this.pedidoNumero.set(numero);
        this.pedidoEmail.set(email);
        this.pedidoTotal.set(total);
        this.copiado.set(false);
    }

    copiarCodigo() {
        const numero = this.pedidoNumero();
        if (!numero) {
            return;
        }
        try {
            const done = () => this.copiado.set(true);
            const clip = (navigator as Navigator & { clipboard?: { writeText(t: string): Promise<void> } }).clipboard;
            if (clip) {
                clip.writeText(numero).then(done).catch(done);
            } else {
                done();
            }
        } catch {
            this.copiado.set(true);
        }
    }

    irRastrear() {
        this.router.navigate(['/seguimiento']);
    }

    irTienda() {
        this.router.navigate(['/']);
    }

    private buildPayload(cartUuid: string, metodoUuid: string): ConfirmarPedidoPayload {
        const f = this.form();
        const isBoleta = f.voucherType === 'boleta';
        const isDelivery = f.deliveryType === 'delivery';
        const isSelf = f.recipientType === 'self';

        return {
            carritoUuid: cartUuid,
            tipoComprobanteId: isBoleta ? 1 : 2,
            tipoDocumentoId: f.docNumber.trim().length === 11 ? 2 : 1,
            numeroDocumento: f.docNumber.trim(),
            nombre: isBoleta ? f.firstName.trim() : null,
            apellidos: isBoleta ? f.lastName.trim() : null,
            razonSocial: isBoleta ? null : `${f.firstName} ${f.lastName}`.trim(),
            correo: f.email.trim(),
            telefono: f.phone.trim(),
            tipoEntregaId: isDelivery ? 1 : 2,
            tipoDestinatarioId: isSelf ? 1 : 2,
            destinatarioNombre: isSelf ? f.firstName.trim() : f.recipientName.trim(),
            destinatarioApellidos: isSelf ? f.lastName.trim() : f.recipientLastName.trim(),
            destinatarioCelular: isSelf ? f.phone.trim() : f.recipientPhone.trim(),
            mensajeTarjeta: f.cardMessage?.trim() || null,
            direccion: isDelivery ? f.address.trim() : null,
            distrito: null,
            referencia: isDelivery ? (f.reference?.trim() || null) : null,
            latitud: f.location?.lat ?? null,
            longitud: f.location?.lng ?? null,
            empresaMetodoPagoUuid: metodoUuid,
            aceptoTerminos: true
        };
    }

    private resolveMetodoPagoUuid(): string | null {
        return this.metodoSeleccionado()?.uuid ?? null;
    }
}
